import type { CommentDto } from "@synthesis/shared-services/comments/external/types";
import type { AttachmentDescriptionDto, FinalDecisionResponse, RequestDetailResponse } from "@synthesis/shared-services/external/application/types";
import type { FileMetaDto as FileServiceMetaDto } from "@synthesis/shared-services/files/external/types";
import type {
  AttachmentDescriptionDto as InternalAttachmentDescriptionDto,
  FileMetaDto as InternalFileMetaDto,
  FinalDecisionApprovalDtoOutcomeEnumKey,
  FinalDecisionApprovalDtoStageEnumKey,
  FinalDecisionDto,
  FinalDecisionRefusalDto,
  FinalDecisionRemarkDto,
  InternalRequestDetailResponse,
  StageAssignmentDtoSlotEnumKey,
} from "@synthesis/shared-services/internal/application/types";
import { paymentViewPaymentFlagEnum, type PaymentViewPaymentFlagEnumKey } from "@synthesis/shared-services/internal/payment/types";
import { makeAutoObservable } from "mobx";
import { makeStoreProvider } from "@synthesis/shared-utils";
import dayjs from "dayjs";
import type { ReactNode } from "react";

import type { ApplicationControlPopupAction } from "./local-components/application-control-popup.lib";
import { parseRegionDepartmentsFromApplication, type RegionDepartment } from "./local-components/application-control-region-departments-modal.lib";
import {
  formatFinalDecisionAuthorName,
  isFinalDecisionAttachmentDescription,
  type FinalDecisionValue,
} from "./local-components/final-decision-tab/final-decision.lib";

import {
  getApplicationDeadlineControlDisplay,
  getApplicationReceivedAtDisplay,
  getApplicationResponsibleDisplay,
  getApplicationReviewDueAtDisplay,
  type ApplicationDeadlineControlState,
} from "../applications-page/applications-page.lib";
import type { ApplicationStatus } from "../../constants";
import { formatPersonName, getInternalInitiatorDisplay } from "../../shared/utils/application.utils";
import { APPLICATION_STATUS_FALLBACK_LABEL } from "../applications-page/applications-page.status";
import type { ApplicationDetailHistoryEntry } from "./application-history.lib";
import { APPLICATION_DETAIL_TAB_DEFINITIONS, type ApplicationPaymentTermsValue } from "./application-detail-tabs.lib";
import {
  parseAdjacentObjectDataFromObjectDescription,
  parseApplicantDataFromAdditionalData,
  parseCreateApplicationAdditionalData,
  parseCrossingObjectDataFromObjectDescription,
  parseCreateApplicationDocumentIdFromAttachmentDescription,
  getCrossingDisplayStationSection,
  getCreateApplicationDocuments,
  getExternalRequestFormFields,
  CREATE_APPLICATION_TYPE_OPTIONS,
  type CreateApplicationCategory,
  type AdjacentObjectDataFormValues,
  type ApplicantDataFormValues,
  type CrossingObjectDataFormValues,
} from "../create-application-page/create-application-page.lib";

export type { ApplicationDetailHistoryEntry };

// ---------------------------------- TYPES ----------------------------------

export type ApplicationDetailTabKey =
  | "object-description"
  | "documents"
  | "applicant"
  | "payer-details"
  | "payment"
  | "review-documents"
  | "comments"
  | "final-decision"
  | "history";

export type ApplicationDetailTab = {
  key: ApplicationDetailTabKey;
  label: string;
};

export type ApplicationDetailField = {
  /** When omitted, `value` is rendered as a self-contained block (no label column). */
  label?: string;
  value: ReactNode;
  /** Full-width content without the label column (e.g. read-only checkbox). */
  fullWidth?: boolean;
};

export type ApplicationDetailDocument = {
  id: string;
  label: string;
  files: ApplicationDetailDocumentFile[];
};

export type ApplicationDetailDocumentFile = {
  id: string;
  fileName: string;
  baseName: string;
  ext?: string | null;
};

export type ApplicationDetailComment = {
  id: string;
  userName: string;
  userInitials: string;
  text: string;
  occurredAt: string;
  canReply?: boolean;
  replies?: ApplicationDetailComment[];
};

export type ApplicationDetailFinalDecision = {
  decisionType: string;
  decisionValue: FinalDecisionValue | null;
  decisionDate: string;
  authorName: string;
  justification: string;
  documents: ApplicationDetailDocumentFile[];
  /** true — решение сохранено как черновик и ещё не направлено на согласование. */
  isDraft: boolean;
  /** Документы скачиваются через POST .../external/files/api/final-decision/download. */
  downloadViaExternalFinalDecision?: boolean;
  /** Документы скачиваются через POST .../internal/files/api/final-decision-document/download. */
  downloadViaFinalDecisionDocument?: boolean;
};

export type ApplicationDetailFinalDecisionRemark = {
  id: string;
  authorName: string;
  createdAt: string;
  comment: string;
  documents: ApplicationDetailDocumentFile[];
};

/** Последний результат согласования этапа итогового решения (из finalDecisionApprovals). */
export type ApplicationDetailApproverApprovalOutcome = Extract<FinalDecisionApprovalDtoOutcomeEnumKey, "APPROVED" | "RETURNED">;

export type ApplicationDetailApproverGroups = {
  ntpChief: string;
  ntpChiefApprovalOutcome: ApplicationDetailApproverApprovalOutcome | null;
  ngOffice: string;
  ngOfficeApprovalOutcome: ApplicationDetailApproverApprovalOutcome | null;
  relatedDepartments: string;
};

export type ApplicationDetailDeadlineControl = {
  receivedAt: string;
  reviewDueAt: string;
  remaining: { label: string; state: ApplicationDeadlineControlState } | null;
  responsible: string;
};

export type ApplicationDetailSecretaryInfo = {
  approverGroups: ApplicationDetailApproverGroups;
  deadlineControl: ApplicationDetailDeadlineControl;
};

export type { RegionDepartment } from "./local-components/application-control-region-departments-modal.lib";

export type ApplicationDetail = {
  requestId: string;
  id: string;
  title: string;
  categoryLabel: string;
  createdAt: string;
  updatedAt: string;
  paymentTerms: string;
  paymentTermsValue: ApplicationPaymentTermsValue | null;
  isPaidServiceRequired: boolean;
  hasInvoice: boolean;
  initiator: string;
  initiatorEmail?: string | null;
  status?: ApplicationStatus;
  processingFlag?: string | null;
  statusLabel: string;
  railway: string;
  stationSection: string;
  junctionLocation: string;
  secretaryInfo?: ApplicationDetailSecretaryInfo;
  regionDepartments?: RegionDepartment[];
  tabs: ApplicationDetailTab[];
  adjacentObjectData: AdjacentObjectDataFormValues | null;
  crossingObjectData: CrossingObjectDataFormValues | null;
  applicantData: ApplicantDataFormValues | null;
  documents: ApplicationDetailDocument[];
  reviewDocuments: ApplicationDetailDocument[];
  comments: ApplicationDetailComment[];
  history: ApplicationDetailHistoryEntry[];
  finalDecision: ApplicationDetailFinalDecision;
  hasFinalDecision: boolean;
};

export type CommentFormValues = {
  comment: string;
};

export type PopupAction = ApplicationControlPopupAction;

// ---------------------------------- CONSTANTS ----------------------------------

export const countApplicationComments = (comments: ApplicationDetailComment[] = []): number =>
  comments.reduce((total, comment) => total + 1 + (comment.replies?.length ?? 0), 0);

export const APPLICATION_COMMENTS_QUERY_KEY = (requestId: string, clientType: "external" | "internal") =>
  ["application-comments", clientType, requestId] as const;

export const APPLICATION_COMMENTS_COUNT_QUERY_KEY = (requestId: string, clientType: "external" | "internal") =>
  ["application-comments-count", clientType, requestId] as const;

// ---------------------------------- UTILS ----------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Да" : "Нет";
  }

  if (typeof value === "bigint") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(formatValue).join(", ") : "—";
  }

  if (isRecord(value)) {
    return JSON.stringify(value, (_, item) => (typeof item === "bigint" ? String(item) : item));
  }

  return String(value);
};

const formatDateTime = (value?: string | null): string => (value ? dayjs(value).format("DD.MM.YYYY, HH:mm") : "—");

const formatFileName = (fileMeta: Pick<FileServiceMetaDto, "baseName" | "ext"> | Pick<InternalFileMetaDto, "baseName" | "ext">): string =>
  fileMeta.ext ? `${fileMeta.baseName}.${fileMeta.ext}` : fileMeta.baseName;

const getApplicantName = (applicant?: { lastName?: string | null; firstName?: string | null; middleName?: string | null } | null): string =>
  [applicant?.lastName, applicant?.firstName, applicant?.middleName].filter(Boolean).join(" ") || "—";

const getInitials = (name: string): string => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.at(0))
    .join("");

  return initials || "—";
};

const getCommentAuthorName = (comment: CommentDto): string =>
  [comment.authorLastName, comment.authorFirstName, comment.authorMiddleName].filter(Boolean).join(" ") || "—";

const mapCommentDto = (comment: CommentDto, canReply = false): ApplicationDetailComment => ({
  id: comment.id,
  userName: getCommentAuthorName(comment),
  userInitials: getInitials(getCommentAuthorName(comment)),
  text: comment.text,
  occurredAt: formatDateTime(comment.createdAt),
  canReply,
});

export const buildApplicationCommentsWithReplies = (
  roots: CommentDto[],
  repliesByRootId: ReadonlyMap<string, CommentDto[]>,
): ApplicationDetailComment[] =>
  roots
    .filter((comment) => !comment.isDeleted)
    .map((comment) => ({
      ...mapCommentDto(comment, true),
      replies: (repliesByRootId.get(comment.id) ?? []).filter((reply) => !reply.isDeleted).map((reply) => mapCommentDto(reply)),
    }));

const getStatusLabel = (status?: ApplicationStatus, displayStatus?: string | null): string => {
  if (displayStatus?.trim()) {
    return displayStatus;
  }

  if (status) {
    return APPLICATION_STATUS_FALLBACK_LABEL[status];
  }

  return "—";
};

const getLatestDateTime = (...values: Array<string | null | undefined>): string | null => {
  const dates = values.filter((value): value is string => Boolean(value));

  if (dates.length === 0) {
    return null;
  }

  return dates.toSorted((firstDate, secondDate) => dayjs(secondDate).valueOf() - dayjs(firstDate).valueOf())[0] ?? null;
};

const parsePaymentTermsValue = (application: Record<string, unknown>): ApplicationPaymentTermsValue | null => {
  const rawValue = application.paymentTermsValue ?? application.paymentType ?? application.displayPaymentTerms;

  if (rawValue === "PENDING_REVIEW" || rawValue === "NO_PAYMENT" || rawValue === "PAID_SERVICE") {
    return rawValue;
  }

  if (rawValue === "На рассмотрении") {
    return "PENDING_REVIEW";
  }

  if (rawValue === "Без оплаты") {
    return "NO_PAYMENT";
  }

  if (rawValue === "Платная услуга") {
    return "PAID_SERVICE";
  }

  return null;
};

const getPaymentTermsDisplay = (paymentTermsValue: ApplicationPaymentTermsValue | null, status?: ApplicationStatus): string => {
  // «На рассмотрении» — значение по умолчанию до принятия решения Аппаратом НГ.
  // До установки признака показываем «—» (STORY-962, FRONT-1319): «На рассмотрении» и прочерк — одно и то же.
  if (paymentTermsValue === "PENDING_REVIEW") {
    return "—";
  }

  if (paymentTermsValue === "NO_PAYMENT") {
    return "Без оплаты";
  }

  if (paymentTermsValue === "PAID_SERVICE") {
    return "Платная услуга";
  }

  if (status === "WAITING_PAYMENT") {
    return "По выставленному счету";
  }

  return "—";
};

const PAYMENT_FLAG_TO_TERMS_VALUE: Record<PaymentViewPaymentFlagEnumKey, ApplicationPaymentTermsValue> = {
  [paymentViewPaymentFlagEnum.UNDER_REVIEW]: "PENDING_REVIEW",
  [paymentViewPaymentFlagEnum.NO_PAYMENT]: "NO_PAYMENT",
  [paymentViewPaymentFlagEnum.PAID]: "PAID_SERVICE",
};

export const getPaymentTermsDisplayByPaymentFlag = (flag?: PaymentViewPaymentFlagEnumKey | null): string | null => {
  if (!flag) {
    return null;
  }

  return getPaymentTermsDisplay(PAYMENT_FLAG_TO_TERMS_VALUE[flag]);
};

const hasApplicationInvoice = (application: Record<string, unknown>, status?: ApplicationStatus): boolean => {
  if (application.hasInvoice === true || application.invoiceIssued === true) {
    return true;
  }

  return status === "WAITING_PAYMENT";
};

const getCategoryFromObjectDescription = (objectDescription: Record<string, unknown> | null | undefined): CreateApplicationCategory | null => {
  if (objectDescription?.category === "adjacent" || objectDescription?.category === "crossing") {
    return objectDescription.category;
  }

  return null;
};

const parseObjectDescriptionRecord = (objectDescription: unknown): Record<string, unknown> | null => {
  if (isRecord(objectDescription)) {
    return objectDescription;
  }

  if (typeof objectDescription !== "string" || !objectDescription.trim()) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(objectDescription);

    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const getCategoryFromRequestSubject = (requestSubject?: InternalRequestDetailResponse["requestSubject"]): CreateApplicationCategory | null => {
  if (requestSubject === "ADJOINING") {
    return "adjacent";
  }

  if (requestSubject === "CROSSING") {
    return "crossing";
  }

  return null;
};

const getCategoryLabel = (category?: CreateApplicationCategory | null, categoryLabel?: string | null): string => {
  if (categoryLabel?.trim()) {
    return categoryLabel;
  }

  if (!category) {
    return "—";
  }

  return CREATE_APPLICATION_TYPE_OPTIONS.find((option) => option.value === category)?.title ?? category;
};

const getDocumentDefinitionTitle = (category: CreateApplicationCategory | null | undefined, documentId: string | null): string | null => {
  if (!documentId) {
    return null;
  }

  return getCreateApplicationDocuments(category ?? null).find((document) => document.id === documentId)?.title ?? null;
};

const getAttachmentFallbackLabel = (description?: string | null): string => {
  const label = description?.replace(/^([^:]+):\s*/, "").trim();

  return label || "Приложенный документ";
};

const toApplicationDetailDocumentFile = (
  id: string,
  fileName: string,
  fileMeta?: Pick<FileServiceMetaDto, "baseName" | "ext"> | Pick<InternalFileMetaDto, "baseName" | "ext">,
): ApplicationDetailDocumentFile => ({
  id,
  fileName,
  baseName: fileMeta?.baseName ?? fileName,
  ext: fileMeta?.ext,
});

const sortDocumentsByDefinitionOrder = (documents: ApplicationDetailDocument[], category: CreateApplicationCategory | null | undefined) => {
  const orderById = new Map(getCreateApplicationDocuments(category ?? null).map((document) => [document.id, document.order]));

  return documents.toSorted((first, second) => {
    const firstOrder = orderById.get(first.id) ?? Number.MAX_SAFE_INTEGER;
    const secondOrder = orderById.get(second.id) ?? Number.MAX_SAFE_INTEGER;

    return firstOrder - secondOrder;
  });
};

/**
 * Оставляет документы только текущего типа заявки (`category`).
 * Заявитель в доработке может сменить тип — документы прежнего типа остаются в архиве,
 * но отображать их не нужно (FRONT-1387).
 */
const filterDocumentsByCurrentCategory = (
  documents: ApplicationDetailDocument[],
  category: CreateApplicationCategory | null | undefined,
): ApplicationDetailDocument[] => {
  if (!category) {
    return documents;
  }

  const currentCategoryDocumentIds = new Set(getCreateApplicationDocuments(category).map((document) => document.id));

  return documents.filter((document) => currentCategoryDocumentIds.has(document.id));
};

const buildExternalDocuments = (
  category: CreateApplicationCategory | null | undefined,
  attachments: AttachmentDescriptionDto[],
  fileMetas: Array<Pick<FileServiceMetaDto, "id" | "baseName" | "ext"> | Pick<InternalFileMetaDto, "id" | "baseName" | "ext">>,
): ApplicationDetailDocument[] => {
  const fileMetaById = new Map(fileMetas.map((fileMeta) => [fileMeta.id, fileMeta]));
  const documentsById = new Map<string, ApplicationDetailDocument>();

  for (const attachment of attachments) {
    const documentId = parseCreateApplicationDocumentIdFromAttachmentDescription(attachment.description);
    const fileMeta = attachment.fileId ? fileMetaById.get(attachment.fileId) : undefined;
    const fileName = fileMeta ? formatFileName(fileMeta) : (attachment.fileId ?? "Файл");
    const groupId = documentId ?? attachment.description?.trim() ?? attachment.fileId ?? fileName;
    const existingDocument = documentsById.get(groupId);
    const file = toApplicationDetailDocumentFile(attachment.fileId ?? fileName, fileName, fileMeta);

    if (existingDocument) {
      existingDocument.files.push(file);
      continue;
    }

    documentsById.set(groupId, {
      id: groupId,
      label: getDocumentDefinitionTitle(category, documentId) ?? getAttachmentFallbackLabel(attachment.description),
      files: [file],
    });
  }

  return sortDocumentsByDefinitionOrder([...documentsById.values()], category);
};

const buildInternalDocuments = (
  category: CreateApplicationCategory | null | undefined,
  attachments: InternalAttachmentDescriptionDto[],
  fileMetas: InternalFileMetaDto[],
  resultFileId?: string | null,
): ApplicationDetailDocument[] => {
  if (attachments.length === 0) {
    return fileMetas
      .filter((file) => file.id !== resultFileId)
      .map((file) => ({
        id: file.id,
        label: "Приложенный документ",
        files: [toApplicationDetailDocumentFile(file.id, formatFileName(file), file)],
      }));
  }

  const applicantDocumentIds = new Set(getCreateApplicationDocuments(category ?? null).map((document) => document.id));

  return buildExternalDocuments(category, attachments, fileMetas).filter((document) => {
    const isApplicantDocument = applicantDocumentIds.has(document.id);
    const isResultFile = document.files.some((file) => file.id === resultFileId);

    return isApplicantDocument && !isResultFile;
  });
};

const buildReviewDocuments = (
  attachments: InternalAttachmentDescriptionDto[],
  fileMetas: InternalFileMetaDto[],
  category: CreateApplicationCategory | null | undefined,
  resultFileId?: string | null,
): ApplicationDetailDocument[] => {
  const applicantDocumentIds = new Set(getCreateApplicationDocuments(category ?? null).map((document) => document.id));
  const fileMetaById = new Map(fileMetas.map((fileMeta) => [fileMeta.id, fileMeta]));
  const documentsById = new Map<string, ApplicationDetailDocument>();

  for (const attachment of attachments) {
    const documentId = parseCreateApplicationDocumentIdFromAttachmentDescription(attachment.description);
    const fileMeta = attachment.fileId ? fileMetaById.get(attachment.fileId) : undefined;

    if (!attachment.fileId || attachment.fileId === resultFileId) {
      continue;
    }

    if (isFinalDecisionAttachmentDescription(attachment.description)) {
      continue;
    }

    if (documentId && applicantDocumentIds.has(documentId)) {
      continue;
    }

    const fileName = fileMeta ? formatFileName(fileMeta) : (attachment.fileId ?? "Файл");
    const groupId = attachment.description?.trim() || attachment.fileId;
    const existingDocument = documentsById.get(groupId);
    const file = toApplicationDetailDocumentFile(attachment.fileId, fileName, fileMeta);

    if (existingDocument) {
      existingDocument.files.push(file);
      continue;
    }

    documentsById.set(groupId, {
      id: groupId,
      label: getAttachmentFallbackLabel(attachment.description),
      files: [file],
    });
  }

  return [...documentsById.values()];
};

const getDecisionTypeLabel = (decision?: string | null, status?: ApplicationStatus): string => {
  if (decision === "APPROVED" || status === "RESOLVED_APPROVED" || status === "TU_ISSUED") {
    return "Положительное решение";
  }

  if (decision === "REJECTED" || status === "RESOLVED_REJECTED" || status === "REJECTED") {
    return "Мотивированный отказ";
  }

  return "—";
};

const buildFinalDecisionDocuments = (
  attachments: Array<{ fileId?: string | null; description?: string | null }>,
  fileMetas: Array<Pick<FileServiceMetaDto, "id" | "baseName" | "ext"> | Pick<InternalFileMetaDto, "id" | "baseName" | "ext">>,
  resultFileId?: string | null,
  resultFileMeta?: Pick<FileServiceMetaDto, "baseName" | "ext"> | Pick<InternalFileMetaDto, "baseName" | "ext">,
): ApplicationDetailDocumentFile[] => {
  const fileMetaById = new Map(fileMetas.flatMap((fileMeta) => (fileMeta.id ? [[fileMeta.id, fileMeta] as const] : [])));
  const documentIds = new Set<string>();
  const documents: ApplicationDetailDocumentFile[] = [];

  const addDocument = (fileId: string, fallbackFileName = "Приложенный документ") => {
    if (documentIds.has(fileId)) {
      return;
    }

    documentIds.add(fileId);
    const fileMeta = fileMetaById.get(fileId);
    const fileName = fileMeta ? formatFileName(fileMeta) : fallbackFileName;

    documents.push(toApplicationDetailDocumentFile(fileId, fileName, fileMeta));
  };

  if (resultFileId) {
    const fileName = resultFileMeta ? formatFileName(resultFileMeta) : "Итоговое решение";
    addDocument(resultFileId, fileName);
  }

  for (const attachment of attachments) {
    if (!attachment.fileId || attachment.fileId === resultFileId) {
      continue;
    }

    if (isFinalDecisionAttachmentDescription(attachment.description)) {
      addDocument(attachment.fileId);
    }
  }

  return documents;
};

const FINALIZED_DECISION_STATUSES: readonly ApplicationStatus[] = ["RESOLVED_APPROVED", "RESOLVED_REJECTED", "TU_ISSUED", "REJECTED"];

export const isFinalDecisionResolvedStatus = (status?: ApplicationStatus): boolean => !!status && FINALIZED_DECISION_STATUSES.includes(status);

const isFinalizedFinalDecision = (resultFileId?: string | null, resolvedAt?: string | null, status?: ApplicationStatus): boolean => {
  if (resultFileId || resolvedAt) {
    return true;
  }

  return isFinalDecisionResolvedStatus(status);
};

const parsePublishedFinalDecisionDocumentName = (name?: string | null): { baseName: string; ext?: string } => {
  const trimmed = name?.trim();

  if (!trimmed) {
    return { baseName: "Документ" };
  }

  const dotIndex = trimmed.lastIndexOf(".");

  if (dotIndex <= 0) {
    return { baseName: trimmed };
  }

  return { baseName: trimmed.slice(0, dotIndex), ext: trimmed.slice(dotIndex + 1) };
};

/** Опубликованное итоговое решение из GET /external/requests/{requestId} → finalDecision. */
const buildPublishedFinalDecisionDocuments = (
  documents: Array<{ fileId?: string | null; name?: string | null }> = [],
): ApplicationDetailDocumentFile[] =>
  documents.flatMap((document) => {
    if (!document.fileId) {
      return [];
    }

    const { baseName, ext } = parsePublishedFinalDecisionDocumentName(document.name);

    return [toApplicationDetailDocumentFile(document.fileId, document.name?.trim() || baseName, { baseName, ext })];
  });

/** Опубликованное итоговое решение из GET /external/requests/{requestId} → finalDecision. */
const buildExternalPublishedFinalDecision = (published: FinalDecisionResponse, isRequestResolved: boolean): ApplicationDetailFinalDecision | null => {
  if (!published.decision) {
    return null;
  }

  return {
    decisionType: getDecisionTypeLabel(published.decision),
    decisionValue: published.decision,
    decisionDate: isRequestResolved && published.decidedAt ? dayjs(published.decidedAt).format("DD.MM.YYYY") : "—",
    authorName: formatFinalDecisionAuthorName(published.author),
    justification: published.justification?.trim() || "—",
    documents: buildPublishedFinalDecisionDocuments(published.documents ?? []),
    isDraft: false,
    downloadViaExternalFinalDecision: true,
  };
};

/** Мотивированный отказ из GET /internal/requests/{requestId}/final-decision/refusal. */
export const buildInternalRefusalFinalDecision = (refusal: FinalDecisionRefusalDto): ApplicationDetailFinalDecision | null => {
  if (!refusal.decision && !refusal.justification && !(refusal.documents ?? []).length && !refusal.decidedAt) {
    return null;
  }

  return {
    decisionType: getDecisionTypeLabel(refusal.decision, "REJECTED"),
    decisionValue: refusal.decision === "APPROVED" || refusal.decision === "REJECTED" ? refusal.decision : "REJECTED",
    decisionDate: refusal.decidedAt ? dayjs(refusal.decidedAt).format("DD.MM.YYYY") : "—",
    authorName: formatFinalDecisionAuthorName(refusal.author),
    justification: refusal.justification?.trim() || "—",
    documents: buildPublishedFinalDecisionDocuments(refusal.documents ?? []),
    isDraft: false,
    downloadViaFinalDecisionDocument: true,
  };
};

const buildFinalDecision = ({
  decision,
  resolvedAt,
  resultFileId,
  resultFileMeta,
  status,
  attachments = [],
  fileMetas = [],
  rejectionReason,
}: {
  decision?: string | null;
  resolvedAt?: string | null;
  resultFileId?: string | null;
  resultFileMeta?: Pick<FileServiceMetaDto, "baseName" | "ext"> | Pick<InternalFileMetaDto, "baseName" | "ext">;
  status?: ApplicationStatus;
  attachments?: Array<{ fileId?: string | null; description?: string | null }>;
  fileMetas?: Array<Pick<FileServiceMetaDto, "id" | "baseName" | "ext"> | Pick<InternalFileMetaDto, "id" | "baseName" | "ext">>;
  rejectionReason?: string | null;
}): ApplicationDetailFinalDecision => {
  const decisionValue = decision === "APPROVED" || decision === "REJECTED" ? decision : null;

  return {
    decisionType: getDecisionTypeLabel(decisionValue, status),
    decisionValue,
    decisionDate: formatDateTime(resolvedAt),
    authorName: "",
    justification: rejectionReason?.trim() || "—",
    documents: buildFinalDecisionDocuments(attachments, fileMetas, resultFileId, resultFileMeta),
    isDraft: false,
  };
};

/**
 * Черновик итогового решения из GET /internal/requests/{requestId}/final-decision.
 * Документы — файлы file-service (final-decision-document), имена приходят из meta.
 */
type FinalDecisionFileMeta = Pick<FileServiceMetaDto, "id" | "baseName" | "ext"> | Pick<InternalFileMetaDto, "id" | "baseName" | "ext">;

type FinalDecisionFileMetaInput = FinalDecisionFileMeta & { isDeleted?: boolean | null };

const buildFinalDecisionFileMetaById = (fileMetas: FinalDecisionFileMetaInput[] = []): Map<string, FinalDecisionFileMeta> =>
  new Map(fileMetas.flatMap((fileMeta) => (fileMeta.id && fileMeta.isDeleted !== true ? [[fileMeta.id, fileMeta] as const] : [])));

const buildResolvedFinalDecisionDocuments = (
  documentIds: string[] = [],
  fileMetas: FinalDecisionFileMetaInput[] = [],
): ApplicationDetailDocumentFile[] => {
  const fileMetaById = buildFinalDecisionFileMetaById(fileMetas);

  return documentIds.flatMap((fileId) => {
    const fileMeta = fileMetaById.get(fileId);

    if (!fileMeta) {
      return [];
    }

    return [toApplicationDetailDocumentFile(fileId, formatFileName(fileMeta), fileMeta)];
  });
};

const getActiveFinalDecisionRemarks = (remarks: FinalDecisionRemarkDto[] = []): Array<FinalDecisionRemarkDto & { id: string }> =>
  remarks
    .filter((remark): remark is FinalDecisionRemarkDto & { id: string } => Boolean(remark.id) && !remark.deletedAt)
    .toSorted((first, second) => dayjs(first.createdAt).valueOf() - dayjs(second.createdAt).valueOf());

/** Замечания текущей версии итогового решения с метаданными вложений. */
export const buildFinalDecisionRemarks = (
  remarks: FinalDecisionRemarkDto[] = [],
  fileMetas: FinalDecisionFileMetaInput[] = [],
): ApplicationDetailFinalDecisionRemark[] => {
  const fileMetaById = buildFinalDecisionFileMetaById(fileMetas);

  return getActiveFinalDecisionRemarks(remarks).flatMap((remark) => {
    const comment = remark.comment?.trim() ?? "";
    const documents = (remark.documentIds ?? []).flatMap((fileId) => {
      const fileMeta = fileMetaById.get(fileId);

      if (!fileMeta) {
        return [];
      }

      return [toApplicationDetailDocumentFile(fileId, formatFileName(fileMeta), fileMeta)];
    });

    if (!comment && documents.length === 0) {
      return [];
    }

    return [
      {
        id: remark.id,
        authorName: formatFinalDecisionAuthorName(remark.author),
        createdAt: formatDateTime(remark.createdAt).split(",")[0]?.trim() || "—",
        comment: comment || "—",
        documents,
      },
    ];
  });
};

/** Документы отправленной версии итогового решения (file-service final-decision-document). */
export const buildFinalDecisionVersionDocuments = (
  documentIds: string[] = [],
  fileMetas: FinalDecisionFileMetaInput[] = [],
): ApplicationDetailDocumentFile[] => buildResolvedFinalDecisionDocuments(documentIds, fileMetas);

export const buildFinalDecisionDraft = (
  draft: FinalDecisionDto,
  fileMetas: FinalDecisionFileMetaInput[] = [],
): ApplicationDetailFinalDecision | null => {
  if (!draft.decision) {
    return null;
  }

  return {
    decisionType: getDecisionTypeLabel(draft.decision),
    decisionValue: draft.decision,
    decisionDate: formatDateTime(draft.createdAt),
    authorName: formatFinalDecisionAuthorName(draft.author),
    justification: draft.justification?.trim() || "—",
    documents: buildResolvedFinalDecisionDocuments(draft.documentIds ?? [], fileMetas),
    isDraft: true,
  };
};

const getRecordDisplayValue = (record: Record<string, unknown>, ...keys: string[]): string => {
  for (const key of keys) {
    const value = record[key];

    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return "—";
};

const getSlotAssigneeDisplay = (application: InternalRequestDetailResponse, slot: StageAssignmentDtoSlotEnumKey): string => {
  const assignment = application.assignments?.find((item) => item.slot === slot);

  return formatPersonName(assignment?.assignee);
};

const getFinalDecisionApprovalOutcome = (
  application: InternalRequestDetailResponse,
  stage: FinalDecisionApprovalDtoStageEnumKey,
): ApplicationDetailApproverApprovalOutcome | null => {
  const outcome = application.finalDecisionApprovals?.find((item) => item.stage === stage)?.outcome;

  return outcome === "APPROVED" || outcome === "RETURNED" ? outcome : null;
};

/**
 * «Дата поступления» в блоке контроля срока — дата первого поступления заявки во внутренний контур.
 * В детальной карточке это `importedAt` (фиксируется один раз, STORY-987); `receivedAt` приходит
 * в деталку не для всех заявок (BAC-2325), поэтому используем его только как запасной вариант.
 */
const getInternalReceivedAtDisplay = (application: InternalRequestDetailResponse): string =>
  application.importedAt ? formatDateTime(application.importedAt) : getApplicationReceivedAtDisplay(application);

const buildSecretaryInfo = (application: InternalRequestDetailResponse): ApplicationDetailSecretaryInfo => {
  const record = application as Record<string, unknown>;
  const deadlineControlDisplay = getApplicationDeadlineControlDisplay(application);

  return {
    approverGroups: {
      ntpChief: getSlotAssigneeDisplay(application, "NTP_HEAD"),
      ntpChiefApprovalOutcome: getFinalDecisionApprovalOutcome(application, "NTP"),
      ngOffice: getSlotAssigneeDisplay(application, "NG_STAFF"),
      ngOfficeApprovalOutcome: getFinalDecisionApprovalOutcome(application, "NG"),
      relatedDepartments: getRecordDisplayValue(
        record,
        "relatedDepartmentsDisplay",
        "displayRelatedDepartments",
        "relatedDepartmentsApproverDisplay",
      ),
    },
    deadlineControl: {
      receivedAt: getInternalReceivedAtDisplay(application),
      reviewDueAt: getApplicationReviewDueAtDisplay(application),
      remaining: deadlineControlDisplay,
      responsible: getApplicationResponsibleDisplay(application),
    },
  };
};

const buildApplicationDetail = (
  core: Pick<
    ApplicationDetail,
    | "requestId"
    | "id"
    | "title"
    | "categoryLabel"
    | "createdAt"
    | "updatedAt"
    | "paymentTerms"
    | "paymentTermsValue"
    | "isPaidServiceRequired"
    | "hasInvoice"
    | "initiator"
    | "initiatorEmail"
    | "status"
    | "processingFlag"
    | "statusLabel"
    | "railway"
    | "stationSection"
    | "junctionLocation"
    | "secretaryInfo"
    | "regionDepartments"
  >,
  detail: Pick<
    ApplicationDetail,
    "adjacentObjectData" | "crossingObjectData" | "applicantData" | "documents" | "reviewDocuments" | "history" | "finalDecision" | "hasFinalDecision"
  >,
  _clientType: "external" | "internal",
): ApplicationDetail => {
  return {
    ...core,
    tabs: APPLICATION_DETAIL_TAB_DEFINITIONS,
    ...detail,
    comments: [],
    finalDecision: detail.finalDecision,
  };
};

const buildApplicationPaymentMeta = (application: Record<string, unknown>, status?: ApplicationStatus) => {
  const paymentTermsValue = parsePaymentTermsValue(application);
  const isPaidServiceRequired = paymentTermsValue === "PAID_SERVICE" || (status === "WAITING_PAYMENT" && application !== undefined);

  return {
    paymentTermsValue,
    paymentTerms: getPaymentTermsDisplay(paymentTermsValue, status),
    isPaidServiceRequired,
    hasInvoice: hasApplicationInvoice(application, status),
  };
};

const hasApplicationFinalDecision = (params: { resultFileId?: string | null; resolvedAt?: string | null; status?: ApplicationStatus }): boolean =>
  isFinalizedFinalDecision(params.resultFileId, params.resolvedAt, params.status);

export const buildExternalApplicationDetail = (
  application: RequestDetailResponse,
  attachments: AttachmentDescriptionDto[] = [],
  fileMetas: FileServiceMetaDto[] = [],
): ApplicationDetail => {
  const status = application.status as ApplicationStatus | undefined;
  const objectDescription = application.objectDescription as Record<string, unknown> | null | undefined;
  const meta = parseCreateApplicationAdditionalData(application.additionalData);
  const category = meta?.category ?? getCategoryFromObjectDescription(objectDescription);
  const crossingObjectData = parseCrossingObjectDataFromObjectDescription(objectDescription);
  const adjacentObjectData = parseAdjacentObjectDataFromObjectDescription(objectDescription);
  const apiFields = getExternalRequestFormFields(application);
  const stationSection =
    formatValue(apiFields.section) ||
    (category === "crossing"
      ? getCrossingDisplayStationSection(crossingObjectData) || formatValue(objectDescription?.stationSection)
      : formatValue(adjacentObjectData?.stationSection ?? objectDescription?.stationSection));
  const railwayValue = apiFields.railway || (typeof objectDescription?.railway === "string" ? objectDescription.railway : "");
  const fileMetaById = new Map(fileMetas.map((fileMeta) => [fileMeta.id, fileMeta]));
  const paymentMeta = buildApplicationPaymentMeta(application as Record<string, unknown>, status);
  const isRequestResolved = isFinalDecisionResolvedStatus(status);
  const publishedFinalDecision = application.finalDecision ? buildExternalPublishedFinalDecision(application.finalDecision, isRequestResolved) : null;
  const finalDecision =
    publishedFinalDecision ??
    buildFinalDecision({
      resolvedAt: isRequestResolved ? application.resolvedAt : null,
      resultFileId: application.resultFileId,
      resultFileMeta: application.resultFileId ? fileMetaById.get(application.resultFileId) : undefined,
      status,
      attachments,
      fileMetas,
      rejectionReason: getRecordDisplayValue(application as Record<string, unknown>, "rejectionReason", "rejectionComment", "comment"),
    });
  const hasFinalDecision =
    Boolean(publishedFinalDecision) ||
    hasApplicationFinalDecision({
      resultFileId: application.resultFileId,
      resolvedAt: application.resolvedAt,
      status,
    });

  return buildApplicationDetail(
    {
      requestId: application.id ?? "",
      id: application.number != null ? `№${String(application.number)}` : (application.id ?? "—"),
      title: application.title ?? "Без названия",
      categoryLabel: getCategoryLabel(category, meta?.categoryLabel),
      createdAt: formatDateTime(application.createdAt),
      updatedAt: formatDateTime(getLatestDateTime(application.createdAt, application.submittedAt, application.resolvedAt)),
      ...paymentMeta,
      initiator: getApplicantName(application.applicant) || formatValue(apiFields.applicantName),
      initiatorEmail: application.initiator?.corporateEmail ?? application.applicantEmail,
      status,
      processingFlag: application.processingFlag,
      statusLabel: getStatusLabel(status, application.displayStatus),
      railway: railwayValue,
      stationSection,
      junctionLocation: formatValue(objectDescription?.junctionLocation),
    },
    {
      adjacentObjectData,
      crossingObjectData,
      applicantData: parseApplicantDataFromAdditionalData(application.additionalData),
      documents: filterDocumentsByCurrentCategory(buildExternalDocuments(category, attachments, fileMetas), category),
      reviewDocuments: [],
      history: [],
      finalDecision,
      hasFinalDecision,
    },
    "external",
  );
};

export const buildInternalApplicationDetail = (
  application: InternalRequestDetailResponse,
  attachments: InternalAttachmentDescriptionDto[] = [],
  draftFinalDecision: ApplicationDetailFinalDecision | null = null,
  refusalFinalDecision: ApplicationDetailFinalDecision | null = null,
): ApplicationDetail => {
  const status = application.status as ApplicationStatus | undefined;
  const objectDescription = parseObjectDescriptionRecord(application.objectDescription);
  const meta = parseCreateApplicationAdditionalData(application.additionalData);
  const category = meta?.category ?? getCategoryFromObjectDescription(objectDescription) ?? getCategoryFromRequestSubject(application.requestSubject);
  const crossingObjectData = parseCrossingObjectDataFromObjectDescription(objectDescription);
  const adjacentObjectData = parseAdjacentObjectDataFromObjectDescription(objectDescription);
  const apiFields = getExternalRequestFormFields(application as unknown as RequestDetailResponse);
  const stationSection =
    formatValue(apiFields.section) ||
    (category === "crossing"
      ? getCrossingDisplayStationSection(crossingObjectData) || formatValue(objectDescription?.stationSection)
      : formatValue(adjacentObjectData?.stationSection ?? objectDescription?.stationSection));
  const railwayValue = apiFields.railway || (typeof objectDescription?.railway === "string" ? objectDescription.railway : "");
  const fileMetas = application.files ?? [];
  const attachmentList =
    attachments.length > 0
      ? attachments
      : fileMetas.map((file) => ({
          fileId: file.id,
          description: null,
        }));
  const resultFileMeta = application.resultFileId ? fileMetas.find((file) => file.id === application.resultFileId) : undefined;
  const paymentMeta = buildApplicationPaymentMeta(application as Record<string, unknown>, status);
  const finalDecision =
    draftFinalDecision ??
    refusalFinalDecision ??
    buildFinalDecision({
      decision: application.finalDecision,
      resolvedAt: isFinalDecisionResolvedStatus(status) ? application.resolvedAt : null,
      resultFileId: application.resultFileId,
      resultFileMeta,
      status,
      attachments: attachmentList,
      fileMetas,
      rejectionReason: getRecordDisplayValue(application as Record<string, unknown>, "rejectionReason", "rejectionComment", "comment"),
    });
  const hasFinalDecision =
    Boolean(draftFinalDecision) ||
    Boolean(refusalFinalDecision) ||
    hasApplicationFinalDecision({
      resultFileId: application.resultFileId,
      resolvedAt: application.resolvedAt,
      status,
    });

  return buildApplicationDetail(
    {
      requestId: application.id ?? "",
      id: application.number != null ? `№${String(application.number)}` : (application.id ?? "—"),
      title: application.title ?? "Без названия",
      categoryLabel: getCategoryLabel(category, meta?.categoryLabel),
      createdAt: formatDateTime(application.importedAt),
      updatedAt: formatDateTime(
        getLatestDateTime(
          application.importedAt,
          application.sentForReviewAt,
          application.resolvedAt,
          ...(application.assignments?.map((assignment) => assignment.assignedAt) ?? []),
        ),
      ),
      ...paymentMeta,
      initiator: getInternalInitiatorDisplay(application as Record<string, unknown>),
      initiatorEmail: application.initiator?.email ?? null,
      status,
      processingFlag: application.processingFlag,
      statusLabel: getStatusLabel(status, application.displayStatus),
      railway: railwayValue,
      stationSection,
      junctionLocation: formatValue(objectDescription?.junctionLocation),
      secretaryInfo: buildSecretaryInfo(application),
      regionDepartments: parseRegionDepartmentsFromApplication(application as Record<string, unknown>),
    },
    {
      adjacentObjectData,
      crossingObjectData,
      applicantData: parseApplicantDataFromAdditionalData(application.additionalData),
      documents: buildInternalDocuments(category, attachments, fileMetas, application.resultFileId),
      reviewDocuments: buildReviewDocuments(attachments, fileMetas, category, application.resultFileId),
      history: [],
      finalDecision,
      hasFinalDecision,
    },
    "internal",
  );
};

// ---------------------------------- STORE ----------------------------------

class PageStore {
  public activeTab: ApplicationDetailTabKey = "object-description";
  public application: ApplicationDetail | null = null;
  public activeReplyId: string | null = null;
  public popupAction: PopupAction | null = null;
  public paymentCanApprove = false;

  constructor() {
    makeAutoObservable(this);
  }

  public setApplication(application: ApplicationDetail) {
    if (this.application?.requestId !== application.requestId) {
      this.activeTab = "object-description";
      this.activeReplyId = null;
    }
    this.application = application;
  }

  public setActiveTab(tab: ApplicationDetailTabKey) {
    this.activeTab = tab;
  }

  public toggleActiveReplyId(commentId: string) {
    this.activeReplyId = this.activeReplyId === commentId ? null : commentId;
  }

  public clearActiveReplyId() {
    this.activeReplyId = null;
  }

  public setComments(comments: ApplicationDetailComment[]) {
    if (!this.application) {
      return;
    }

    this.application.comments = comments;
  }

  public setPopupAction(action: PopupAction | null) {
    this.popupAction = action;
  }

  public setPaymentCanApprove(value: boolean) {
    this.paymentCanApprove = value;
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
